import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Key, Wrench, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { authApi } from "../lib/api";
import { formatApiErrorDetail } from "../contexts/AuthContext";
import { useNavigationHistory } from "../contexts/NavigationHistoryContext";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { goBack } = useNavigationHistory();
  const [params] = useSearchParams();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const t = params.get("token");
    if (t) setToken(t);
  }, [params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await authApi.resetPassword(token.trim(), password);
      setSuccess(true);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <Wrench className="h-4.5 w-4.5 text-white" />
          </div>
          <div className="font-bold text-slate-900">ServicePro</div>
        </div>

        <h2 className="text-[26px] font-bold text-slate-900 mb-1.5">Choose a new password</h2>
        <p className="text-[13.5px] text-slate-500 mb-6">Paste your reset token and pick a new password.</p>

        {success ? (
          <div data-testid="reset-success" className="px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-[13px] text-emerald-800">
            Password updated. Redirecting to sign in…
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Reset token</label>
              <div className="relative">
                <Key className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  required
                  data-testid="reset-token-input"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Paste the token from email/backend logs"
                  className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-[12.5px] font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">New password</label>
              <div className="relative">
                <Lock className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="password"
                  required
                  minLength={6}
                  data-testid="reset-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                />
              </div>
            </div>

            {error && (
              <div data-testid="reset-error" className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[12.5px] text-red-700">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="reset-submit-button"
              className="w-full h-10 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[14px] font-semibold"
            >
              {submitting ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}

        <button
          type="button"
          onClick={() => goBack("/login")}
          data-testid="reset-back-link"
          className="mt-6 inline-flex items-center gap-1.5 text-[12.5px] text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
        </button>
      </div>
    </div>
  );
}
